import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ACCOUNTS_IN_MOTION_LIMIT,
  accountsInMotionFilterCaption,
  accountsInMotionView,
  fetchMarketPlayers,
  fetchMarketComposition,
  fetchMarketAccountsInMotion,
} from '@/lib/marketExperienceApi';
import {
  formatCategoryLabel,
  PLAYERS_CHART_TOOLTIP,
  PLAYERS_SHARE_THRESHOLD,
  LMS_VIEW,
  categoryAccountsTotal,
  consolidatePlayersVendors,
  lmsCategoryFromPayload,
  lmsChipVisible,
  motionCardBucket,
  motionHasDisplayedSignalScores,
  evidenceSignedScore,
  filterAccountsInMotionRows,
  hasBuyingEvidence,
  playerCategoryMatchesDefault,
  playersExaminedCaption,
  resolvePlayersDefaultCategory,
  toPlayersBarPercents,
  visiblePlayerCategories,
  sisCategoryFromPayload,
  mktLabel,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import MarketHelpLabel from './MarketHelpLabel';

const FINANCIAL_COLORS = {
  High: '#00C2C2',
  Moderate: '#0A1A2F',
  Low: '#5C6B7A',
};
const AI_COLORS = ['#D1D5DB', '#89CDD4', '#5C6B7A', '#1C485C', '#0A1A2F'];
const OTHER_BAR_FILL = '#CBD5E1';
const NAMED_BAR_FILL = '#00C2C2';
// Rendered via t('mktShareCaption', { pct: SHARE_CAPTION_PCT }) — translated at render, not here.
const SHARE_CAPTION_PCT = Math.round(PLAYERS_SHARE_THRESHOLD * 100);

/** Display name for the consolidated long-tail bar ("Other" / "Other (n)"); vendor names pass through. */
function playersBarDisplayName(entry, t) {
  if (!entry || entry.key !== 'other' || entry.kind !== 'other') return entry?.name;
  const m = String(entry.name || '').match(/^Other(?: \((\d+)\))?$/);
  if (!m) return entry.name;
  return m[1] ? t('mktOtherN', { n: m[1] }) : mktLabel(t, 'Other');
}

function asPercent(share, count, total) {
  if (share != null && share !== '') {
    const n = Number(share);
    if (!Number.isNaN(n)) return n <= 1 ? Math.round(n * 100) : Math.round(n);
  }
  if (total) return Math.round((Number(count || 0) / total) * 100);
  return 0;
}

function ShareBar({ data, emptyLabel }) {
  const { t } = useTranslation();
  const rows = toPlayersBarPercents(data).map((r) => {
    const name = playersBarDisplayName(r, t);
    return name === r.name ? r : { ...r, name };
  });
  if (!rows.length) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        <p>{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.2} />
          <XAxis type="number" domain={[0, 100]} tick={false} axisLine={false} tickLine={false} height={0} />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 12, fill: '#5C6B7A' }}
            stroke="#5C6B7A"
            width={120}
          />
          <Tooltip
            formatter={(value, _name, props) => {
              const count = props?.payload?.count;
              const inSam = props?.payload?.inSam;
              const sam = inSam != null ? ` · ${t('mktNInYourMarket', { n: inSam })}` : '';
              return [`${value}%${count != null ? ` · ${count}` : ''}${sam}`, t('mktInstitutionShare')];
            }}
          />
          <Bar dataKey="share" background={{ fill: '#eee' }} radius={[0, 4, 4, 0]}>
            {rows.map((entry) => (
              <Cell
                key={entry.key || entry.name}
                fill={entry.kind === 'other' ? OTHER_BAR_FILL : NAMED_BAR_FILL}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function vendorsFromPlayers(payload, t) {
  const lms = lmsCategoryFromPayload(payload);
  const vendors = lms?.vendors || payload?.vendors || [];
  const total = categoryAccountsTotal(lms || payload);
  return {
    rows: consolidatePlayersVendors(vendors, total),
    total,
    caption: playersExaminedCaption(lms || payload, t),
    cells: lms?.total_cells ?? payload?.total_cells,
  };
}

function MomentumChip({ label, value }) {
  if (value == null || value === '') return null;
  const n = Number(value);
  const up = n > 0;
  const down = n < 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] tabular-nums font-medium ${
        up ? 'bg-[#ECFFFE] text-[#0A1A2F]' : down ? 'bg-amber-50 text-amber-900' : 'bg-gray-100 text-[#5C6B7A]'
      }`}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-70">{label}</span>
      {up ? '+' : ''}{n}
    </span>
  );
}

function MotionEvidenceItem({ item }) {
  const { t } = useTranslation();
  if (!item) return null;
  const contrib = evidenceSignedScore(item);
  const type = item.signal_type || item.kind;
  const catLabel = item.category_label || (item.category ? formatCategoryLabel(item.category) : null);
  return (
    <div className="rounded-md bg-gray-50 px-2.5 py-2 space-y-1">
      <p className="text-[11px] text-[#5C6B7A]">
        {item.when ? <span className="tabular-nums">{item.when}</span> : null}
        {item.when && (type || item.vendor || catLabel || contrib != null) ? ' · ' : ''}
        {[
          type ? String(type).replace(/_/g, ' ') : null,
          item.vendor,
          catLabel,
          contrib != null ? `${contrib > 0 ? '+' : ''}${contrib}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {item.quote ? (
        <p className="text-xs text-[#0A1A2F] leading-snug">“{item.quote}”</p>
      ) : null}
      {item.source_url ? (
        <a
          href={item.source_url}
          target="_blank"
          rel="noreferrer"
          className="inline-block text-[11px] text-[#00C2C2] hover:underline"
        >
          {t('source')}
        </a>
      ) : null}
    </div>
  );
}

function MotionCard({ row, filterKey = null }) {
  const { t } = useTranslation();
  const bucket = filterKey === 'buying' && hasBuyingEvidence(row)
    ? 'buying'
    : filterKey === 'toward'
      ? 'toward'
      : filterKey === 'under_pressure'
        ? 'pressure'
        : motionCardBucket(row);
  const toward = bucket === 'toward';
  const pressure = bucket === 'pressure';
  const buying = bucket === 'buying';
  const moved = row.what_moved;
  const evidence = Array.isArray(row.evidence) ? row.evidence.filter(Boolean) : [];
  const accent = buying ? '#0A1A2F' : toward ? '#00C2C2' : pressure ? '#D97706' : '#94A3B8';
  const directionLabel = buying
    ? t('mktBuyingEvidence')
    : toward
      ? t('mktInOurFavour')
      : pressure
        ? t('mktRunningAgainstUs')
        : t('mktFlat');
  const signalN = evidence.length || row.signal_count;
  const showMomentumChips = !motionHasDisplayedSignalScores(row);

  return (
    <li className="rounded-lg border border-gray-200 bg-white overflow-hidden flex">
      <div className="w-1.5 shrink-0" style={{ background: accent }} aria-hidden />
      <div className="flex-1 min-w-0 p-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0A1A2F] truncate">{row.account_name}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <span
                className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded"
                style={{ background: `${accent}18`, color: accent }}
              >
                {directionLabel}
              </span>
              {signalN != null ? (
                <span className="text-[11px] text-[#5C6B7A]">
                  {signalN === 1 ? t('mktOneSignal', { n: signalN }) : t('mktNSignals', { n: signalN })}
                </span>
              ) : null}
            </div>
          </div>
          {showMomentumChips ? (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <MomentumChip label={t('mkt4w')} value={row.momentum_4w} />
            <MomentumChip label={t('mkt12w')} value={row.momentum_12w} />
          </div>
          ) : null}
        </div>

        {evidence.length > 0 ? (
          <div className="space-y-1.5">
            {evidence.map((item, i) => (
              <MotionEvidenceItem key={`${item.when || 'e'}-${i}`} item={item} />
            ))}
          </div>
        ) : moved ? (
          <MotionEvidenceItem item={moved} />
        ) : null}

        {typeof row.buying_evidence === 'string' && row.buying_evidence.trim() ? (
          <p className="text-xs text-[#0A1A2F]">{row.buying_evidence}</p>
        ) : row.buying_evidence && typeof row.buying_evidence === 'object' && row.buying_evidence.quote ? (
          <p className="text-xs text-[#0A1A2F]">“{row.buying_evidence.quote}”</p>
        ) : null}
      </div>
    </li>
  );
}

const MOTION_BADGES = [
  { key: 'toward', label: 'In our favour', labelKey: 'mktInOurFavour', field: 'moving_toward', color: '#00C2C2' },
  { key: 'under_pressure', label: 'Running against us', labelKey: 'mktRunningAgainstUs', field: 'under_pressure', color: '#D97706' },
  { key: 'buying', label: 'Buying evidence', labelKey: 'mktBuyingEvidence', field: 'with_buying_evidence', color: '#0A1A2F' },
];

function AccountsInMotion({ tenantId, lens, country, countryLabel }) {
  const { t } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [direction, setDirection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !lens) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    fetchMarketAccountsInMotion(tenantId, lens, country, {
      limit: ACCOUNTS_IN_MOTION_LIMIT,
      direction,
    })
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Could not load accounts in motion');
        setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, lens, country, direction]);

  const view = accountsInMotionView(payload);
  const rows = filterAccountsInMotionRows(view.rows, direction);
  const filtered = Boolean(direction);
  const matched = filtered
    ? (direction === 'buying'
      ? view.with_buying_evidence
      : direction === 'toward'
        ? view.moving_toward
        : direction === 'under_pressure'
          ? view.under_pressure
          : view.matched)
    : view.matched;
  const showing = rows.length;
  const total = view.total;
  const filterCaption = filtered ? accountsInMotionFilterCaption({ showing, matched }) : null;
  const emptyGroup = filtered && rows.length === 0;
  const showList = !emptyGroup && rows.length > 0;

  if (!tenantId || !lens) return null;
  if (loading && !payload) {
    return <div className="h-64 bg-gray-100 rounded-md animate-pulse" />;
  }
  if (error && !payload) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error === 'Could not load accounts in motion' ? t('mktCouldNotLoadAccountsInMotion') : error}</div>
    );
  }
  if (!payload) return null;

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-[#0A1A2F]">
          <MarketHelpLabel
            label={t('mktAccountsInMotion')}
            hint={t('mktAccountsInMotionHint')}
          />
        </CardTitle>
        <p className="text-sm text-[#5C6B7A]">
          {t('mktNAccounts', { n: total ?? 0 })} {inScope(countryLabel, t)}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {MOTION_BADGES.map((s) => {
            const active = direction === s.key;
            const value = view[s.field];
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={active}
                onClick={() => setDirection((prev) => (prev === s.key ? null : s.key))}
                className={`rounded-lg px-3 py-2.5 text-center transition ${
                  active ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={active ? { boxShadow: `inset 0 0 0 2px ${s.color}` } : undefined}
              >
                <p className="text-2xl font-semibold tabular-nums" style={{ color: s.color }}>
                  {value ?? '—'}
                </p>
                <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A] mt-0.5">{s.labelKey ? t(s.labelKey) : s.label}</p>
              </button>
            );
          })}
        </div>

        {filtered && !emptyGroup && filterCaption ? (
          <p className="text-sm text-[#5C6B7A]">{filterCaption}</p>
        ) : null}

        {emptyGroup ? (
          <p className="text-sm text-[#5C6B7A]">{t('mktNoAccountsInGroup')}</p>
        ) : showList ? (
          <ul className={`space-y-2 max-h-[28rem] overflow-y-auto pr-0.5 ${loading ? 'opacity-60' : ''}`}>
            {rows.map((row, i) => (
              <MotionCard key={row.account_id || row.account_name || i} row={row} filterKey={direction} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-[#5C6B7A]">{t('mktNoAccountsInMotionFor', { scope: scopeLabelText(countryLabel, t) })}</p>
        )}
      </CardContent>
    </Card>
  );
}

function formatScopeCountries(countries, fallbackLabel) {
  const list = (countries || []).filter(Boolean);
  if (!list.length) return fallbackLabel || 'this market';
  if (list.length === 1) return list[0];
  if (list.length <= 3) return list.join(', ');
  return `across ${list.length} countries`;
}

/**
 * Display text for a scope label built by formatScopeCountries. The label itself stays English
 * (inScope tests it for "across"); only the rendered text is translated when `t` is passed.
 */
function scopeLabelText(countryLabel, t) {
  const label = String(countryLabel || '');
  if (typeof t !== 'function') return label;
  if (label === 'this market') return t('mktThisMarket');
  const m = label.match(/^across (\d+) countries$/);
  if (m) return t('mktAcrossNCountries', { n: m[1] });
  return label;
}

/** Drop a leading "in" when the scope already starts with "across". */
function inScope(countryLabel, t) {
  const label = String(countryLabel || '').trim();
  if (!label) return '';
  if (/^across\b/i.test(label)) return scopeLabelText(label, t);
  if (typeof t === 'function') {
    return label === 'this market' ? t('mktInThisMarket') : t('mktInScope', { label });
  }
  return `in ${label}`;
}

export default function MarketPlayers({ tenantId, lens, countryFilter = 'all' }) {
  const { t } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [lmsPayload, setLmsPayload] = useState(null);
  const [composition, setComposition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(null);

  const country = countryFilter === 'all' ? undefined : countryFilter;

  useEffect(() => {
    let cancelled = false;
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    /* [2026-11-04] ONE /players CALL, NOT TWO. The second asked for `category: 'lms'`, which the API ignores — it always
     * returns the lens categories plus the LMS/SIS context — so the page paid for the same 4-8 s answer twice.
     * The LMS chip reads its category out of the same payload (lmsCategoryFromPayload finds it by key). */
    Promise.all([
      lens ? fetchMarketPlayers(tenantId, lens, country ? { country } : {}).catch(() => null) : Promise.resolve(null),
      lens ? fetchMarketComposition(tenantId, lens, country).catch(() => null) : Promise.resolve(null),
    ])
      .then(([data, comp]) => {
        const lms = data;
        if (cancelled) return;
        setPayload(data);
        setLmsPayload(lms);
        setComposition(comp);
        const visible = visiblePlayerCategories(data);
        setView(resolvePlayersDefaultCategory(data, visible, { lmsVisible: lmsChipVisible(lms) }));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Could not load players');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, lens, country]);

  const scopeCountries = payload?.countries_in_scope || lmsPayload?.countries_in_scope || [];
  const countryLabel = formatScopeCountries(
    country ? [country] : scopeCountries,
    'this market',
  );

  const categories = useMemo(() => visiblePlayerCategories(payload), [payload]);
  const showLmsChip = lmsChipVisible(lmsPayload);
  // [2026-11-04] SIS is context like LMS, so its chip comes after LMS, always last (Tiago).
  const sisCat = useMemo(() => sisCategoryFromPayload(payload), [payload]);
  const current = useMemo(() => {
    if (view === LMS_VIEW) return null;
    const pool = sisCat ? [...categories, sisCat] : categories;
    return pool.find((c) => c.category === view || c.key === view)
      || pool.find((c) => playerCategoryMatchesDefault(c, view))
      || null;
  }, [categories, sisCat, view]);
  const lmsMode = view === LMS_VIEW;

  const examinedCaption = playersExaminedCaption(current, t);
  const inPlaceTotal = categoryAccountsTotal(current);
  const inPlaceData = useMemo(
    () => consolidatePlayersVendors(current?.vendors || [], inPlaceTotal),
    [current, inPlaceTotal],
  );
  const lmsData = useMemo(() => vendorsFromPlayers(lmsPayload, t), [lmsPayload, t]);
  const chartData = lmsMode ? lmsData.rows : inPlaceData;

  const marketTotal = composition?.total_accounts;
  const aiRows = useMemo(() => {
    const levels = composition?.ai_readiness?.levels || [];
    return levels.map((l, i) => ({
      name: l.label || l.level || `L${i + 1}`,
      count: l.count || 0,
      inSam: l.in_sam,
      share: asPercent(l.share, l.count, marketTotal),
      color: AI_COLORS[i] || '#5C6B7A',
    })).filter((r) => r.count > 0);
  }, [composition, marketTotal]);

  const financialRows = useMemo(() => {
    const bands = composition?.financial_capacity?.bands || [];
    return bands.map((b) => ({
      name: b.band || b.label || b.key,
      count: b.count || 0,
      inSam: b.in_sam,
      share: asPercent(b.share, b.count, marketTotal),
      color: FINANCIAL_COLORS[b.band || b.label] || '#94A3B8',
    })).filter((r) => r.count > 0);
  }, [composition, marketTotal]);

  const topInSam = composition?.ai_readiness?.top_in_sam || [];
  const highestInSam = composition?.financial_capacity?.highest_in_sam || [];
  const aiHeadline = composition?.ai_readiness?.headline;
  const aiDefs = composition?.ai_readiness?.definitions || {};
  const aiTooltip = [
    aiDefs.scale,
    ...Object.entries(aiDefs)
      .filter(([k]) => k !== 'scale')
      .map(([k, v]) => `${k}: ${v}`),
  ].filter(Boolean).join('\n');
  const finHeadline = composition?.financial_capacity?.headline;
  const finDefs = composition?.financial_capacity?.definitions || {};
  const finTooltip = [
    finDefs.scale,
    ...Object.entries(finDefs)
      .filter(([k]) => k !== 'scale')
      .map(([k, v]) => `${k}: ${v}`),
  ].filter(Boolean).join('\n');

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error === 'Could not load players' ? t('mktCouldNotLoadPlayers') : error}</div>
    );
  }

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
      ) : (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">
              <MarketHelpLabel
                label={t('mktWhoIsInPlace')}
                hint={lmsMode
                  ? t('mktLmsShareHint')
                  : mktLabel(t, PLAYERS_CHART_TOOLTIP)}
              />
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((c) => (
                <button
                  key={c.category || c.key}
                  type="button"
                  onClick={() => setView(c.category || c.key)}
                  className={`text-xs px-3 py-1 rounded-full ${
                    !lmsMode && (c.category === view || c.key === view) ? 'bg-[#00C2C2] text-white' : 'bg-gray-100'
                  }`}
                >
                  {formatCategoryLabel(c.label || c.category)}
                </button>
              ))}
              {showLmsChip ? (
                <button
                  type="button"
                  onClick={() => setView(LMS_VIEW)}
                  className={`text-xs px-3 py-1 rounded-full ${
                    lmsMode ? 'bg-[#00C2C2] text-white' : 'bg-gray-100'
                  }`}
                  title={t('mktLmsChipTitle')}
                >
                  LMS
                </button>
              ) : null}
              {sisCat ? (
                <button
                  type="button"
                  onClick={() => setView(sisCat.category || sisCat.key)}
                  className={`text-xs px-3 py-1 rounded-full ${
                    !lmsMode && (sisCat.category === view || sisCat.key === view) ? 'bg-[#00C2C2] text-white' : 'bg-gray-100'
                  }`}
                >
                  SIS
                </button>
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ShareBar
            key={`${countryFilter}-${view}`}
            data={chartData}
            emptyLabel={lmsMode ? t('mktNoLmsDataFor', { scope: scopeLabelText(countryLabel, t) }) : t('mktNoVendorDataForCategory')}
          />
          <p className="text-xs text-[#5C6B7A] mt-3">
            {lmsMode ? (
              <>
                {lmsData.caption
                  ? `${lmsData.caption} ${inScope(countryLabel, t)}.`
                  : `${t('mktNLmsCells', { n: lmsData.cells ?? lmsData.total })} ${inScope(countryLabel, t)}.`}
                {' '}{t('mktShareCaption', { pct: SHARE_CAPTION_PCT })}
              </>
            ) : (
              <>
                {examinedCaption}
                {examinedCaption ? ' · ' : ''}
                {t('mktShareCaption', { pct: SHARE_CAPTION_PCT })}
              </>
            )}
          </p>
        </CardContent>
      </Card>
      )}

      <AccountsInMotion
        tenantId={tenantId}
        lens={lens}
        country={country}
        countryLabel={countryLabel}
      />

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
          <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">
              <MarketHelpLabel
                label={t('mktAiReadinessIndex')}
                hint={aiTooltip || t('mktAiReadinessWholeMarketHint')}
              />
            </CardTitle>
            <p className="text-sm text-[#5C6B7A]">
              {aiHeadline || `${t('mktWholeMarket')} ${inScope(countryLabel, t)}${marketTotal != null ? ` · ${t('mktNAccounts', { n: marketTotal })}` : ''}`}
            </p>
          </CardHeader>
          <CardContent>
            {aiRows.length === 0 ? (
              <p className="text-sm text-[#5C6B7A] h-56 flex items-center justify-center">
                {t('mktNoAiCompositionFor', { scope: scopeLabelText(countryLabel, t) })}
              </p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aiRows} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.2} />
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#5C6B7A" width={100} />
                    <Tooltip
                      formatter={(value, _n, props) => [
                        `${value}% · ${t('mktNAccounts', { n: props?.payload?.count })} · ${t('mktNInYourMarket', { n: props?.payload?.inSam ?? 0 })}`,
                        t('mktShare'),
                      ]}
                    />
                    <Bar dataKey="share" radius={[0, 4, 4, 0]}>
                      {aiRows.map((row) => (
                        <Cell key={row.name} fill={row.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {topInSam.length > 0 && (
              <p className="text-xs text-[#5C6B7A] mt-2">
                {t('mktHighestInYourMarket', { names: topInSam.slice(0, 3).map((a) => a.account_name || a.name).filter(Boolean).join(', ') })}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A1A2F]">
              <MarketHelpLabel
                label={t('mktFinancialCapacity')}
                hint={finTooltip || t('mktFinancialWholeMarketHint')}
              />
            </CardTitle>
            <p className="text-sm text-[#5C6B7A]">
              {finHeadline || `${t('mktWholeMarket')} ${inScope(countryLabel, t)}`}
            </p>
          </CardHeader>
          <CardContent>
            {financialRows.length === 0 ? (
              <p className="text-sm text-[#5C6B7A] h-56 flex items-center justify-center">
                {t('mktNoFinancialCompositionFor', { scope: scopeLabelText(countryLabel, t) })}
              </p>
            ) : (
              <div className="h-56 flex items-center">
                <ResponsiveContainer width="50%" height="100%">
                  <PieChart>
                    <Pie data={financialRows} dataKey="count" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={1}>
                      {financialRows.map((row) => (
                        <Cell key={row.name} fill={row.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [t('mktNAccounts', { n: value }), mktLabel(t, name)]} />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="flex-1 space-y-1.5 pr-2">
                  {financialRows.map((row) => (
                    <li key={row.name} className="flex items-center justify-between text-sm gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: row.color }} />
                        <span className="truncate text-[#0A1A2F]">{mktLabel(t, row.name)}</span>
                      </span>
                      <span className="font-medium text-[#0A1A2F] tabular-nums">{row.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {highestInSam.length > 0 && (
              <p className="text-xs text-[#5C6B7A] mt-2">
                {t('mktHighestInYourMarket', { names: highestInSam.slice(0, 3).map((a) => a.account_name || a.name).filter(Boolean).join(', ') })}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
